import type { CategoryGroup, EquipmentProfileItemDto, PageGroup, StringTranslation } from './types';
import { PAGE_PREFIX_LABEL_KEYS, PAGE_PREFIX_ORDER } from './consts';
import moment from 'moment';

const resolveTranslation = (translations: StringTranslation[], fallback: string, culture = 'en-US'): string =>
  translations.find(tr => tr.culture === culture)?.text ?? fallback;

const getPagePrefix = (key: string): string => key.split('.')[1] ?? '';

export const groupItemsByPage = (items: EquipmentProfileItemDto[], resolveLabel: (prefix: string) => string): PageGroup[] => {
  const pageMap = new Map<string, Map<number, CategoryGroup>>();

  items.forEach(item => {
    const prefix = getPagePrefix(item.key);
    if (!PAGE_PREFIX_LABEL_KEYS[prefix]) return;
    if (!pageMap.has(prefix)) pageMap.set(prefix, new Map());
    const catMap = pageMap.get(prefix)!;
    if (!catMap.has(item.category.id)) {
      catMap.set(item.category.id, {
        categoryId: item.category.id,
        categoryTitle: resolveTranslation(item.category.titleTranslations, item.category.title),
        categorySubTitle: item.category.subTitle ? resolveTranslation(item.category.subTitleTranslations, item.category.subTitle) : null,
        categorySeq: item.category.seq,
        items: [],
      });
    }
    catMap.get(item.category.id)!.items.push(item);
  });

  const pages: PageGroup[] = [];
  for (const prefix of PAGE_PREFIX_ORDER) {
    if (!pageMap.has(prefix)) continue;
    const catMap = pageMap.get(prefix)!;
    const categories = Array.from(catMap.values())
      .sort((a, b) => a.categorySeq - b.categorySeq)
      .map(cat => ({
        ...cat,
        items: [...cat.items].sort((a, b) => a.title.localeCompare(b.title)),
      }));
    pages.push({ pagePrefix: prefix, pageLabel: resolveLabel(prefix), categories });
  }
  return pages;
};

export const buildSavedQtys = (items: EquipmentProfileItemDto[]): Record<string, number> => {
  const map: Record<string, number> = {};
  items.forEach(item => {
    map[item.inventoryItemId] = item.isOverridden ? item.quantity : item.defaultQuantity;
  });
  return map;
};

export const formatTimeSpan = (timeSpan?: string | null): string => {
  if (!timeSpan) return '—';
  const dayMatch = timeSpan.match(/^(\d+)\.(\d{2}):(\d{2}):(\d{2})$/);
  if (dayMatch) {
    const hours = parseInt(dayMatch[2], 10);
    const minutes = dayMatch[3];
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes} ${period} (+1)`;
  }
  const match = timeSpan.match(/^(\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return timeSpan;
  const hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes} ${period}`;
};

export const formatTime = (time: string, format?: string) => {
  return time ? moment(time, 'HH:mm:ss').format(format ?? 'h:mm A') : undefined;
};
